import {
  Button as MuiButton,
  type ButtonOwnProps as MuiButtonOwnProps,
  type PolymorphicComponent,
  type PolymorphicProps,
} from '@mui/base'
import { unstable_useControlled as useControlled } from '@mui/utils'
import { clsx } from 'clsx'
import {
  type ComponentProps,
  type ElementType,
  type MouseEventHandler,
  type ReactElement,
  useCallback,
} from 'react'

import { IconCheck } from '@redteclab/icons'

type ChipOwnProps = Omit<
  MuiButtonOwnProps,
  'href' | 'rootElementName' | 'to' | 'type'
> & {
  /**
   * Whether the element should be selected (uncontrolled).
   */
  defaultSelected?: boolean

  href?: string

  /**
   * Icon placed before the children.
   */
  icon?: ReactElement<ComponentProps<'svg'>, 'svg'> | false | null

  /**
   * Whether the element should be selected (controlled).
   */
  selected?: boolean

  /**
   * The size of the component.
   * @default medium
   */
  // eslint-disable-next-line perfectionist/sort-union-types -- semantic order
  size?: 'small' | 'medium' | 'large'
}

interface ChipTypeMap<RootComponentType extends ElementType = 'button'> {
  defaultComponent: RootComponentType
  props: ChipOwnProps
}

export type ChipProps<
  RootComponentType extends ElementType = ChipTypeMap['defaultComponent'],
> = PolymorphicProps<ChipTypeMap<RootComponentType>, RootComponentType>

/**
 * Chips are compact elements that represent an input, attribute, or action.
 */
export const Chip: PolymorphicComponent<ChipTypeMap> = (props: ChipProps) => {
  const {
    children,
    className,
    defaultSelected,
    href,
    icon,
    onClick: onClickProp,
    selected: selectedProp,
    size = 'medium',
    ...other
  } = props

  const [selected, setSelected] = useControlled<boolean | undefined>({
    controlled: selectedProp,
    default: defaultSelected,
    name: 'Chip',
    state: 'selected',
  })

  const uncontrolled = selectedProp === undefined

  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      onClickProp?.(event)

      if (uncontrolled) {
        setSelected((prevValue) => !prevValue)
      }
    },
    [onClickProp, setSelected, uncontrolled],
  )

  return (
    <MuiButton
      className={clsx(
        'chip',
        size === 'small' && 'chip_small',
        size === 'medium' && 'chip_medium',
        size === 'large' && 'chip_large',
        selected && 'chip_selected',
        className,
      )}
      href={href}
      onClick={onClick}
      {...other}
      // `aria-pressed` is allowed only under role `button`, skip if `href` is passed. Goes last to prevent `aria-pressed` overwrite
      aria-pressed={href ? undefined : Boolean(selected)}
    >
      {selected || icon ? (
        <span className="chip__icon">{icon ?? <IconCheck />}</span>
      ) : null}
      {children}
    </MuiButton>
  )
}
